import {
  SearchTenantsQuery,
  WorkflowManagerConfigType,
} from "generated-graphql/graphql";

/**
 * Entity types for workflow manager config rules
 */
export type RuleEntity =
  | "state"
  | "stateRevision"
  | "stateNewlyNotReporting"
  | "facility"
  | "facilityRevision"
  | "facilityNewlyNotReporting"
  | "tenant"
  | "tenantForState";

/**
 * Rule type definition for the Add Rule modal
 */
export type RuleType = {
  action: "allow" | "deny";
  entity: RuleEntity;
  label: string;
  configType: WorkflowManagerConfigType;
};

/**
 * Tenant option from the search query
 */
export type TenantOption = NonNullable<
  SearchTenantsQuery["tenants"]["items"]
>[number];

/**
 * State option for dropdowns
 */
export type StateOption = {
  value: string;
  label: string;
};

/**
 * Props for the AddRuleModal component
 */
export interface AddRuleModalProps {
  open: boolean;
  onClose: () => void;
  onRuleAdded: () => void;
}

// Entity type helper functions
export function isStateEntity(
  entity: RuleEntity | undefined
): entity is "state" | "stateRevision" | "stateNewlyNotReporting" {
  return (
    entity === "state" ||
    entity === "stateRevision" ||
    entity === "stateNewlyNotReporting"
  );
}

export function isFacilityEntity(
  entity: RuleEntity | undefined
): entity is "facility" | "facilityRevision" | "facilityNewlyNotReporting" {
  return (
    entity === "facility" ||
    entity === "facilityRevision" ||
    entity === "facilityNewlyNotReporting"
  );
}

export function isTenantEntity(
  entity: RuleEntity | undefined
): entity is "tenant" {
  return entity === "tenant";
}

export function isTenantForStateEntity(
  entity: RuleEntity | undefined
): entity is "tenantForState" {
  return entity === "tenantForState";
}

export function isTenantRelatedEntity(
  entity: RuleEntity | undefined
): entity is "tenant" | "tenantForState" {
  return entity === "tenant" || entity === "tenantForState";
}

/**
 * Combined rule options grouped by Allow first, then Deny
 */
export const RULE_TYPE_OPTIONS: RuleType[] = [
  // Allow options
  {
    action: "allow",
    entity: "state",
    label: "Allow - State",
    configType: WorkflowManagerConfigType.AllowState,
  },
  {
    action: "allow",
    entity: "stateRevision",
    label: "Allow - State Revision",
    configType: WorkflowManagerConfigType.AllowStateRevision,
  },
  {
    action: "allow",
    entity: "stateNewlyNotReporting",
    label: "Allow - State (Newly Not Reporting)",
    configType: WorkflowManagerConfigType.AllowStateNewlyNotReporting,
  },
  {
    action: "allow",
    entity: "facilityNewlyNotReporting",
    label: "Allow - Facility (Newly Not Reporting)",
    configType: WorkflowManagerConfigType.AllowFacilityNewlyNotReporting,
  },
  {
    action: "allow",
    entity: "facility",
    label: "Allow - Facility",
    configType: WorkflowManagerConfigType.AllowFacility,
  },
  {
    action: "allow",
    entity: "facilityRevision",
    label: "Allow - Facility Revision",
    configType: WorkflowManagerConfigType.AllowFacilityRevision,
  },
  // Deny options
  {
    action: "deny",
    entity: "facility",
    label: "Deny - Facility",
    configType: WorkflowManagerConfigType.DenyFacility,
  },
  {
    action: "deny",
    entity: "facilityRevision",
    label: "Deny - Facility Revision",
    configType: WorkflowManagerConfigType.DenyFacilityRevision,
  },
  {
    action: "deny",
    entity: "tenant",
    label: "Deny - Tenant (all states)",
    configType: WorkflowManagerConfigType.DenyTenant,
  },
  {
    action: "deny",
    entity: "tenant",
    label: "Deny - Tenant Revision (all states)",
    configType: WorkflowManagerConfigType.DenyTenantRevision,
  },
  {
    action: "deny",
    entity: "tenantForState",
    label: "Deny - Tenant for State",
    configType: WorkflowManagerConfigType.DenyTenantForState,
  },
  {
    action: "deny",
    entity: "tenantForState",
    label: "Deny - Tenant for State Revision",
    configType: WorkflowManagerConfigType.DenyTenantForStateRevision,
  },
  {
    action: "deny",
    entity: "tenant",
    label: "Deny - Tenant (Newly Not Reporting)",
    configType: WorkflowManagerConfigType.DenyTenantNewlyNotReporting,
  },
  {
    action: "deny",
    entity: "tenantForState",
    label: "Deny - Tenant for State (Newly Not Reporting)",
    configType: WorkflowManagerConfigType.DenyTenantForStateNewlyNotReporting,
  },
  {
    action: "deny",
    entity: "facility",
    label: "Deny - Facility (Newly Not Reporting)",
    configType: WorkflowManagerConfigType.DenyFacilityNewlyNotReporting,
  },
];
